import React, { useState, useEffect } from 'react';
import { Drawer, IconButton, Tabs, Tab, Box, Typography, Skeleton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import BookmarkBorderOutlinedIcon from '@mui/icons-material/BookmarkBorderOutlined';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import House from '../../Assets/images/house.png';
import ScheduleVisitation from './ScheduleVisitation';
import getServices from '../../Services/get-service';
import postService from '../../Services/post-service';
import Constant from '../../Constant';

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
      className="flex-1 overflow-y-auto"
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function PropertyDetailsDrawer({ open, handleClose, property, onBuyClick }) {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [details, setDetails] = useState(null);
  const [isBookmarked, setIsBookmarked] = useState(false);

  useEffect(() => {
    if (open && property) {
      setLoading(true);
      getServices.getInvestmentById(property.id).then(res => {
        if (res.data.statusCode === 200) {
          const p = res.data.data;
          setDetails({
            ...property,
            ...p,
            title: p.title || property.title,
            location: p.locality || property.location,
            type: null,
            targetDetail: p.price ? `₦${Constant.FormatNumber(p.price)}` : null,
            minSalesValue: p.minSalesValue || null,
            expected: p.expected || null,
            annualYield: p.annualYield || null,
          });
        }
        setLoading(false);
      }).catch(() => {
        setLoading(false);
        setDetails(property);
      });

      // Check bookmark status
      getServices.getSavedProduct(100, 0).then(savedRes => {
        if (savedRes.data.statusCode === 200) {
          const rawData = savedRes.data.data;
          let allSaved = [];
          if (Array.isArray(rawData)) allSaved = rawData;
          else if (rawData && Array.isArray(rawData.savedProperties)) allSaved = rawData.savedProperties;
          else if (rawData && Array.isArray(rawData.content)) allSaved = rawData.content;
          else if (rawData && Array.isArray(rawData.projects)) allSaved = rawData.projects;
          else if (rawData && Array.isArray(rawData.data)) allSaved = rawData.data;

          const savedIds = allSaved.map(p => String(p.propertyId || p.projectId || p.id || p.property?.id || p.project?.id));
          setIsBookmarked(savedIds.includes(String(property.id)));
        }
      });
    } else {
      setDetails(null);
    }
  }, [open, property]);

  const handleBookmarkToggle = () => {
    const idStr = String(property.id);
    if (isBookmarked) {
      setIsBookmarked(false);
      postService.removePropertyBuyLater(idStr).catch(() => setIsBookmarked(true));
    } else {
      setIsBookmarked(true);
      postService.addPropertyBuyLater(idStr).catch(() => setIsBookmarked(false));
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // If no property is selected
  if (!property) return null;

  const displayData = details || property;
  const isRental = displayData.type === 'Rental';

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: { xs: '100%', sm: '100%', md: 450 },
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: '#F9FAFB'
        },
      }}
    >
      {loading ? (
        <div className="w-full h-full flex flex-col relative">
          <div className="absolute top-4 left-4 right-4 flex justify-between items-center z-10">
            <div
              onClick={handleClose}
              className="w-8 h-8 rounded-full bg-black bg-opacity-60 flex items-center justify-center cursor-pointer text-white hover:bg-opacity-80"
            >
              <CloseIcon fontSize="small" />
            </div>
          </div>
          <Skeleton variant="rectangular" height={250} className="w-full flex-shrink-0" />
          <div className="p-4 space-y-4 flex-1">
            <Skeleton variant="rectangular" height={80} className="rounded-xl" />
            <Skeleton variant="rectangular" height={150} className="rounded-xl" />
            <Skeleton variant="text" width="60%" />
            <Skeleton variant="rectangular" height={200} className="rounded-xl" />
          </div>
        </div>
      ) : (
        <>
          <div className="relative h-[250px] w-full flex-shrink-0">
            <img src={property.image || ""} alt="" className="w-full h-full object-cover" />

            <div className="absolute top-4 left-4 right-4 flex justify-between items-center z-10">
              <div
                onClick={handleClose}
                className="w-8 h-8 rounded-full bg-black bg-opacity-60 flex items-center justify-center cursor-pointer text-white hover:bg-opacity-80"
              >
                <CloseIcon fontSize="small" />
              </div>
              <div className="flex gap-2">
                <div
                  className="w-8 h-8 rounded-full bg-white bg-opacity-80 flex items-center justify-center cursor-pointer text-gray-700 hover:bg-white"
                  onClick={handleBookmarkToggle}
                >
                  {isBookmarked ? (
                    <BookmarkIcon fontSize="small" style={{ color: '#00BC67' }} />
                  ) : (
                    <BookmarkBorderOutlinedIcon fontSize="small" />
                  )}
                </div>
                <div className="w-8 h-8 rounded-full bg-white bg-opacity-80 flex items-center justify-center cursor-pointer text-gray-700 hover:bg-white">
                  <ShareOutlinedIcon fontSize="small" />
                </div>
              </div>
            </div>

            {/* Title Overlay */}
            <div className="absolute bottom-4 left-4 right-4 z-10">
              {displayData.type && (
                <span className={`text-[10px] px-2 py-0.5 rounded text-white ${displayData.typeColor || 'bg-primary-color'} mb-2 inline-block`}>
                  {displayData.type}
                </span>
              )}
              <h2 className="text-white text-xl font-bold mb-1">{displayData.title}</h2>
              <p className="text-white text-xs flex items-center opacity-90">
                <LocationOnOutlinedIcon style={{ fontSize: '14px', marginRight: '2px' }} />
                {displayData.location}
              </p>
            </div>

            {/* Dark Gradient Overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent opacity-80 pointer-events-none"></div>
          </div>
          {/* Black Info Bar */}
          <div className="bg-[#2B2B2B] flex justify-between items-center px-6 py-3 flex-shrink-0">
            <div>
              <p className="text-[#A1A1A1] text-[10px] uppercase">Per Unit</p>
              <p className="text-white text-[14px] font-bold">₦{Constant.FormatNumber(displayData.unitPrice || 0)}</p>
            </div>
            <div>
              <p className="text-[#A1A1A1] text-[10px] uppercase text-center">Remaining</p>
              <p className="text-white text-[14px] font-bold text-center">
                {displayData.unitsRemaining} / <span className="text-white text-[14px] font-bold text-center">{displayData.totalUnitsAvailable}</span>
              </p>
            </div>
            <div>
              <p className="text-[#A1A1A1] text-[10px] uppercase text-right">Term</p>
              <p className="text-white text-[14px] font-bold text-right">{displayData.term || '0'}</p>
            </div>
          </div>

          {/* Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'white' }}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              variant="fullWidth"
              TabIndicatorProps={{ style: { backgroundColor: '#00BC67' } }}
              sx={{
                '& .MuiTab-root': {
                  textTransform: 'none',
                  minWidth: 'auto',
                  padding: '12px 0',
                  fontWeight: 600,
                  color: '#808285',
                  fontSize: '12px'
                },
                '& .Mui-selected': {
                  color: '#1A1A1B !important',
                }
              }}
            >
              <Tab label="Overview" />
              <Tab label="Video" />
              <Tab label="Map" />
              <Tab label="Schedule Visitation" />
            </Tabs>
          </Box>

          {/* Overview Tab Content */}
          <TabPanel value={tabValue} index={0}>
            {/* Funding Progress */}
            <div className="bg-white rounded-xl p-4 mb-4 border border-gray-100">
              <div className="flex justify-between items-center mb-2">
                <p className="text-[12px] font-bold text-back-color2">Funding Progress</p>
                <p className="text-[12px] font-bold text-back-color2">{displayData.percentSold || 0}%</p>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-1.5 mb-4">
                <div
                  className="bg-primary-color h-1.5 rounded-full"
                  style={{ width: `${displayData.percentSold || 0}%` }}
                ></div>
              </div>

              <div className="bg-[#F0FDF4] p-3 rounded-lg mb-3">
                <p className="text-[#00BC67] text-[10px] leading-relaxed">
                  {isRental
                    ? "Investors in this property earn annual rental dividends based on occupancy rate. Earliest Liquidation: 2 years from purchase date"
                    : "This property is being developed for sale. Investors are paid out when the property sells. Early exit available via Secondary Market."
                  }
                </p>
              </div>
              {displayData.type && (
                <p className="text-[10px] text-gray-400">
                  Investments type: {isRental ? "Rental - Co-ownership, annual dividends." : "Sales- property under development, sold on completion"}
                </p>
              )}
            </div>

            <p className="text-[11px] font-medium text-back-color2 mb-4 px-1">
              Units are selling fast - only {displayData.unitsLeft || 0} left
            </p>

            {/* Investments Details */}
            <div className="bg-white rounded-xl p-4 mb-4 border border-gray-100">
              <p className="text-[12px] font-bold text-back-color2 mb-4">Investments Details</p>

              <div className="space-y-3">
                <div className="flex justify-between">
                  <p className="text-[11px] text-gray-500">Per Unit</p>
                  <p className="text-[11px] font-bold text-back-color2">₦{Constant.FormatNumber(displayData.unitPrice || 0)}</p>
                </div>
                <div className="flex justify-between">
                  <p className="text-[11px] text-gray-500">Units Remaining</p>
                  <p className="text-[11px] font-bold text-back-color2">{displayData.unitsRemaining}</p>
                </div>
                <div className="flex justify-between">
                  <p className="text-[11px] text-gray-500">Term</p>
                  <p className="text-[11px] font-bold text-back-color2">{displayData.termDetail || '24 months'}</p>
                </div>
                <div className="flex justify-between">
                  <p className="text-[11px] text-gray-500">Target capital</p>
                  <p className="text-[11px] font-bold text-back-color2">₦{Constant.FormatNumber(displayData.price || 0)}</p>
                </div>

                {isRental ? (
                  <div className="flex justify-between">
                    <p className="text-[11px] text-gray-500">Annual yield</p>
                    <p className="text-[11px] font-bold text-back-color2">{displayData.annualYield || null}</p>
                  </div>
                ) : (
                  <>
                    <div className="flex justify-between">
                      <p className="text-[11px] text-gray-500">Projected min.sales value</p>
                      <p className="text-[11px] font-bold text-back-color2">{displayData.minSalesValue || null}</p>
                    </div>
                    <div className="flex justify-between">
                      <p className="text-[11px] text-gray-500">Expected</p>
                      <p className="text-[11px] font-bold text-back-color2">{displayData.expected || null}</p>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Developer Info */}
            <div className="bg-white rounded-xl p-3 border border-gray-100 flex justify-between items-center mb-8">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#E6F8F0] rounded-lg flex items-center justify-center">
                  <span className="text-[#00BC67] text-xl">🏢</span>
                </div>
                <div>
                  <p className="text-[12px] font-bold text-back-color2 mb-0.5">{displayData.developer || ''}</p>
                  <p className="text-[10px] text-gray-500">Verified Developers</p>
                </div>
              </div>
              <CheckCircleOutlineIcon sx={{ color: '#00BC67', fontSize: 20 }} />
            </div>
          </TabPanel>

          {/* Placeholders for other tabs */}
          <TabPanel value={tabValue} index={1}>
            <div className="flex flex-col items-center justify-center h-40 text-gray-400 text-sm">
              Video Player Placeholder
            </div>
          </TabPanel>
          <TabPanel value={tabValue} index={2}>
            <div className="flex flex-col items-center justify-center h-40 text-gray-400 text-sm">
              Map View Placeholder
            </div>
          </TabPanel>
          <TabPanel value={tabValue} index={3}>
            <ScheduleVisitation propertyId={property.id} />
          </TabPanel>
        </>
      )}

      {/* Sticky Footer */}
      <div className="p-4 bg-white border-t border-gray-100 mt-auto flex-shrink-0">
        <button
          onClick={onBuyClick}
          className="w-full bg-primary-color text-white font-bold py-3.5 rounded-lg text-sm hover:bg-[#00a359] transition-colors"
        >
          Buy Units
        </button>
        <p className="text-center text-[10px] text-gray-400 mt-2">
          Availability updates in real time
        </p>
      </div>

    </Drawer>
  );
}

export default PropertyDetailsDrawer;
